import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Global cross-tab session guard.
 *  • When the user logs out in one tab (session_token removed from localStorage),
 *    every other tab of the SAME browser reacts to the `storage` event and is
 *    redirected to /login instantly.
 *  • Any tab that regains focus / becomes visible re-validates the session
 *    (/auth/me). If it is gone (401), that tab logs out immediately — so an
 *    already-open page logs out the moment the user clicks / focuses it.
 * Public / not-logged-in tabs (no session_token) are never touched.
 */
const SessionSync = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const goLogin = () => {
      if (window.location.pathname !== "/login") navigate("/login");
    };

    const onStorage = (e) => {
      if (e.key === "session_token" && !e.newValue) goLogin();
    };

    const revalidate = async () => {
      let token = null;
      try { token = localStorage.getItem("session_token"); } catch { /* blocked */ }
      if (!token) return; // not a logged-in tab → leave public pages alone
      try {
        await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      } catch (err) {
        if (err?.response?.status === 401) {
          try { localStorage.removeItem("session_token"); } catch { /* blocked */ }
          goLogin();
        }
      }
    };

    const onVisibility = () => { if (!document.hidden) revalidate(); };

    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", revalidate);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", revalidate);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [navigate]);

  return null;
};

export default SessionSync;
